/**
 * `MyCertifications` — elenco delle certificazioni possedute dall'utente.
 *
 * Rotta `/certifications` (qualsiasi utente autenticato). Mostra le
 * certificazioni assegnate al proprio profilo (manuali o importate da Credly).
 *
 * Il collegamento del profilo Credly vive nella pagina Profilo (`/profile`).
 * Qui, se l'utente ha già collegato un profilo Credly, mostriamo una scorciatoia
 * per importare direttamente le proprie certificazioni; altrimenti un rimando al
 * profilo per collegarlo.
 *
 * Endpoint usati: `GET /api/me/certifications`,
 * `POST /api/me/certifications/import-credly`.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApiClient } from '../api/useApiClient';
import { useAuth } from '../auth/AuthProvider';
import type {
  ImportCredlyResponse,
  ImportLinkedinResponse,
  SkillDto,
  UserCertificationDto,
} from '../shared/dto';
import {
  CertificationCard,
  certExpiryStatus,
  certIssuedTime,
  formatCertDate,
  sortCertificationsByIssuedDesc,
} from '../components/CertificationCard';
import {
  Alert,
  Badge,
  Button,
  type Column,
  DataTable,
  EmptyState,
  Field,
  Icon,
  Loading,
  PageHeader,
  TextInput,
} from '../components/ui';

const myCertificationsKey = ['me', 'certifications'] as const;
const skillsKey = ['skills'] as const;

/**
 * Valore del filtro per skill:
 * - `'all'`: tutte le certificazioni;
 * - `<skillId>`: solo le certificazioni collegate a quella skill.
 */
type SkillFilter = 'all' | string;

/** Riga normalizzata della tabella delle certificazioni nascoste. */
interface HiddenCertificationRow {
  key: string;
  certificationId: string;
  name: string;
  issuer: string;
  /** Data di conseguimento già formattata (o `—` se assente). */
  issuedLabel: string;
  /** Timestamp (ms) di conseguimento per l'ordinamento, o `null` se assente. */
  issuedTime: number | null;
  /** Data di scadenza ISO, o `null`. */
  expiresAt: string | null;
}

/**
 * Filtra le certificazioni per query testuale (nome o ente) e per skill
 * collegata. Funzione pura. Il filtro per skill è applicato prima di quello
 * testuale.
 */
export function filterMyCertifications(
  rows: readonly UserCertificationDto[],
  query: string,
  skill: SkillFilter = 'all',
): UserCertificationDto[] {
  let result: readonly UserCertificationDto[] = rows;

  if (skill !== 'all') {
    // Filtrando per una skill mostriamo solo le certificazioni valide collegate:
    // una certificazione scaduta non "copre" più la skill (né dà bonus).
    result = result.filter(
      (c) => c.skillIds.includes(skill) && certExpiryStatus(c.expiresAt) !== 'expired',
    );
  }

  const q = query.trim().toLowerCase();
  if (q.length === 0) return [...result];
  return result.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      (c.issuer ?? '').toLowerCase().includes(q),
  );
}

export default function MyCertifications() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const [query, setQuery] = useState('');
  const [skillFilter, setSkillFilter] = useState<SkillFilter>('all');
  const [importResult, setImportResult] = useState<ImportCredlyResponse | null>(null);
  const [linkedinImportResult, setLinkedinImportResult] =
    useState<ImportLinkedinResponse | null>(null);
  // L'elenco delle certificazioni nascoste è dietro un accordion, chiuso di
  // default (come la sezione "Altre certificazioni" del CV).
  const [showHidden, setShowHidden] = useState(false);

  // `true` se l'utente ha collegato un profilo Credly: solo in tal caso
  // mostriamo la scorciatoia di import direttamente qui (altrimenti un rimando
  // al profilo per collegarlo).
  const hasLinkedCredly = (me?.credlyProfileUrl ?? '').trim() !== '';
  // Stato del collegamento LinkedIn: ora esposto da /api/me (UserDto).
  const hasLinkedLinkedin = me?.linkedinLinked === true;

  const certificationsQuery = useQuery({
    queryKey: myCertificationsKey,
    queryFn: () => api.get<UserCertificationDto[]>('/api/me/certifications'),
  });

  // Import delle proprie certificazioni dal profilo Credly collegato. Al
  // termine si invalida la lista così le nuove certificazioni compaiono subito.
  const importCredly = useMutation({
    mutationFn: () =>
      api.post<ImportCredlyResponse>('/api/me/certifications/import-credly'),
    onSuccess: (result) => {
      setImportResult(result);
      void queryClient.invalidateQueries({ queryKey: myCertificationsKey });
    },
  });

  // Import delle proprie certificazioni da LinkedIn (quando l'account è
  // collegato). Specchia l'import Credly: invalida la lista al termine.
  const importLinkedin = useMutation({
    mutationFn: () =>
      api.post<ImportLinkedinResponse>('/api/me/certifications/import-linkedin'),
    onSuccess: (result) => {
      setLinkedinImportResult(result);
      void queryClient.invalidateQueries({ queryKey: myCertificationsKey });
    },
  });
  // Catalogo skill per risolvere gli id in nomi nel select del filtro.
  const skillsQuery = useQuery({
    queryKey: skillsKey,
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });

  // Tutte le assegnazioni restituite dall'API (incluse le nascoste, che il
  // backend espone solo nella propria vista con il flag `hidden`).
  const allCertifications = certificationsQuery.data ?? [];
  // Certificazioni **visibili**: la lista principale e i filtri operano solo su
  // queste. Le nascoste vanno nella sezione dedicata in fondo.
  const certifications = useMemo(
    () => allCertifications.filter((c) => c.hidden !== true),
    [allCertifications],
  );
  // Certificazioni **nascoste** dall'utente, ordinate per data di conseguimento
  // decrescente come la lista principale.
  const hiddenCertifications = useMemo(
    () =>
      sortCertificationsByIssuedDesc(
        allCertifications.filter((c) => c.hidden === true),
        (c) => c.issuedAt,
        (c) => c.name,
      ),
    [allCertifications],
  );

  // Ripristino di una certificazione nascosta: la rende di nuovo visibile.
  // Al termine invalida la lista così si sposta tra le visibili.
  const restoreCertification = useMutation({
    mutationFn: (certificationId: string) =>
      api.post<void>(
        `/api/me/certifications/${encodeURIComponent(certificationId)}/restore`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myCertificationsKey });
    },
  });

  // Righe della tabella delle nascoste: forma normalizzata (nome/ente/date)
  // come la sezione "Altre certificazioni" del CV.
  const hiddenRows = useMemo<HiddenCertificationRow[]>(
    () =>
      hiddenCertifications.map((c) => {
        const issued = formatCertDate(c.issuedAt);
        return {
          key: c.certificationId,
          certificationId: c.certificationId,
          name: c.name.trim() || 'Certificazione',
          issuer: c.issuer?.trim() ?? '',
          issuedLabel: issued === '—' ? '—' : issued,
          issuedTime: certIssuedTime(c.issuedAt),
          expiresAt: c.expiresAt,
        };
      }),
    [hiddenCertifications],
  );

  // Colonne della tabella delle nascoste: le stesse di "Altre certificazioni"
  // (nome come link al dettaglio personale, ente, conseguita, scadenza) più la
  // colonna azione "Rendi visibile".
  const hiddenColumns = useMemo<Column<HiddenCertificationRow>[]>(
    () => [
      {
        key: 'name',
        header: 'Certificazione',
        render: (row) => (
          <Link
            to={`/assessment/certifications/${encodeURIComponent(row.certificationId)}`}
            state={{ from: '/assessment/certifications' }}
            className="coverage-name__link"
            aria-label={`Apri il dettaglio di ${row.name}`}
          >
            {row.name}
            <Icon name="external" size={14} className="coverage-name__link-icon" />
          </Link>
        ),
        sortable: true,
        sortAccessor: (row) => row.name.toLowerCase(),
      },
      {
        key: 'issuer',
        header: 'Ente',
        render: (row) => (row.issuer === '' ? '—' : row.issuer),
        sortable: true,
        sortAccessor: (row) => row.issuer.toLowerCase(),
      },
      {
        key: 'issuedAt',
        header: 'Conseguita',
        render: (row) => row.issuedLabel,
        sortable: true,
        sortComparator: (a, b, direction) => {
          if (a.issuedTime === b.issuedTime) return 0;
          if (a.issuedTime === null) return 1;
          if (b.issuedTime === null) return -1;
          const diff = a.issuedTime - b.issuedTime;
          return direction === 'desc' ? -diff : diff;
        },
      },
      {
        key: 'expiresAt',
        header: 'Scadenza',
        render: (row) => {
          const status = certExpiryStatus(row.expiresAt);
          if (status === 'none') return 'Nessuna';
          if (status === 'expired') {
            return <Badge tone="danger">{formatCertDate(row.expiresAt)}</Badge>;
          }
          if (status === 'expiring') {
            return (
              <Badge tone="warning">In scadenza {formatCertDate(row.expiresAt)}</Badge>
            );
          }
          return formatCertDate(row.expiresAt);
        },
      },
      {
        key: 'actions',
        header: 'Azioni',
        className: 'admin-table__actions-col',
        render: (row) => (
          <div className="admin-item__actions">
            <Button
              type="button"
              size="sm"
              iconOnly
              aria-label={`Rendi visibile ${row.name}`}
              title="Rendi visibile"
              loading={
                restoreCertification.isPending &&
                restoreCertification.variables === row.certificationId
              }
              disabled={restoreCertification.isPending}
              onClick={() => restoreCertification.mutate(row.certificationId)}
            >
              <Icon name="eye" />
            </Button>
          </div>
        ),
      },
    ],
    [restoreCertification],
  );

  // Opzioni del filtro: solo le skill collegate alle certificazioni possedute
  // (id distinti), risolte in nome e ordinate alfabeticamente. Se una skill non
  // è più a catalogo viene esclusa (nessun nome da mostrare).
  const skillOptions = useMemo(() => {
    const skillNameById = new Map(
      (skillsQuery.data ?? []).map((s) => [s.id, s.name]),
    );
    const ownedSkillIds = new Set<string>();
    for (const c of certifications) {
      for (const id of c.skillIds) ownedSkillIds.add(id);
    }
    return [...ownedSkillIds]
      .map((id) => ({ id, name: skillNameById.get(id) }))
      .filter((o): o is { id: string; name: string } => o.name !== undefined)
      .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }));
  }, [certifications, skillsQuery.data]);

  // Risolutore id skill → nome, per mostrare le skill collegate come chip.
  const skillNameById = useMemo(() => {
    const byId = new Map((skillsQuery.data ?? []).map((s) => [s.id, s.name]));
    return (id: string) => byId.get(id);
  }, [skillsQuery.data]);

  const visible = useMemo(
    () =>
      sortCertificationsByIssuedDesc(
        filterMyCertifications(certifications, query, skillFilter),
        (c) => c.issuedAt,
        (c) => c.name,
      ),
    [certifications, query, skillFilter],
  );

  return (
    <section className="my-certifications" aria-labelledby="my-certifications-title">
      <PageHeader
        title="Le mie certificazioni"
        titleId="my-certifications-title"
        subtitle="Le certificazioni assegnate al tuo profilo."
        actions={
          <>
            {hasLinkedCredly ? (
              <Button
                type="button"
                variant="primary"
                loading={importCredly.isPending}
                disabled={importCredly.isPending}
                onClick={() => importCredly.mutate()}
              >
                Importa da Credly
              </Button>
            ) : (
              <Link to="/profile" className="ui-btn">
                Collega Credly
              </Link>
            )}{' '}
            {/* LinkedIn: se collegato, import diretto da qui (come Credly);
                altrimenti scorciatoia al blocco LinkedIn del profilo per
                collegarsi (il collegamento OAuth vive nel Profilo, Req 8.5). */}
            {hasLinkedLinkedin ? (
              <Button
                type="button"
                variant="primary"
                loading={importLinkedin.isPending}
                disabled={importLinkedin.isPending}
                onClick={() => importLinkedin.mutate()}
              >
                Importa da LinkedIn
              </Button>
            ) : (
              <Link to="/profile" className="ui-btn">
                Collega LinkedIn
              </Link>
            )}
          </>
        }
      />

      {importCredly.isError && (
        <Alert tone="error" onDismiss={() => importCredly.reset()}>
          Import non riuscito. Verifica che il profilo Credly collegato sia
          pubblico e valido, poi riprova.
        </Alert>
      )}
      {importResult && (
        <Alert tone="success" onDismiss={() => setImportResult(null)}>
          Import completato: {importResult.imported} nuove, {importResult.skipped} già
          presenti, {importResult.removed} rimosse, {importResult.createdCertifications}{' '}
          aggiunte al catalogo aziendale (su {importResult.fetched} badge letti).
        </Alert>
      )}
      {importLinkedin.isError && (
        <Alert tone="error" onDismiss={() => importLinkedin.reset()}>
          Import da LinkedIn non riuscito. Se la sessione è scaduta, ricollega
          LinkedIn dal Profilo e riprova.
        </Alert>
      )}
      {linkedinImportResult && (
        <Alert tone="success" onDismiss={() => setLinkedinImportResult(null)}>
          Import da LinkedIn completato: {linkedinImportResult.imported} nuove,{' '}
          {linkedinImportResult.skipped} già presenti, {linkedinImportResult.removed}{' '}
          rimosse, {linkedinImportResult.createdCertifications} aggiunte al catalogo
          aziendale (su {linkedinImportResult.fetched} certificazioni lette).
        </Alert>
      )}

      {!certificationsQuery.isLoading &&
        !certificationsQuery.isError &&
        certifications.length > 0 && (
          <Field
            id="my-certifications-search"
            label="Cerca certificazione"
            hint="Filtra per skill collegata e cerca per nome o ente."
          >
            <div className="competence-search">
              <span className="competence-search__department user-admin__select">
                <select
                  id="my-certifications-skill"
                  aria-label="Filtra certificazioni per skill"
                  value={skillFilter}
                  disabled={skillsQuery.isLoading || skillOptions.length === 0}
                  onChange={(e) => setSkillFilter(e.target.value)}
                >
                  <option value="all">Tutte le skill</option>
                  {skillOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </span>
              <TextInput
                id="my-certifications-search"
                className="competence-search__input"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca per nome o ente…"
                autoComplete="off"
              />
            </div>
          </Field>
        )}

      {certificationsQuery.isLoading ? (
        <Loading>Caricamento certificazioni…</Loading>
      ) : certificationsQuery.isError ? (
        <Alert tone="error">Impossibile caricare le tue certificazioni.</Alert>
      ) : certifications.length === 0 ? (
        <EmptyState title="Nessuna certificazione">
          Non hai ancora certificazioni. Collega Credly e importa dalla pagina{' '}
          <Link to="/profile">Profilo</Link>, oppure chiedi a un amministratore di
          assegnartene.
        </EmptyState>
      ) : visible.length === 0 ? (
        <EmptyState title="Nessun risultato">
          Nessuna certificazione corrisponde ai filtri selezionati.
        </EmptyState>
      ) : (
        <ul className="certification-list">
          {visible.map((c) => (
            <li
              key={c.certificationId}
              className="certification-card certification-card--linked"
            >
              {/* "Stretched link": copre l'intera card per aprirne il dettaglio
                  personale. Gli anchor interni della card (es. link al badge)
                  restano cliccabili grazie a un z-index superiore. */}
              <Link
                to={`/assessment/certifications/${encodeURIComponent(c.certificationId)}`}
                className="certification-card__stretched-link"
                aria-label={`Apri il dettaglio di ${c.name}`}
              />
              {/* La rimozione dal proprio profilo è spostata nella pagina di
                  dettaglio della certificazione: qui la card è solo un link. */}
              <CertificationCard
                certification={c}
                skillNameById={skillNameById}
              />
            </li>
          ))}
        </ul>
      )}

      {/* Certificazioni nascoste: dietro un accordion (separatore cliccabile),
          poi la lista. Compare solo se ce ne sono e solo nella propria vista
          (il backend espone le nascoste al solo proprietario). */}
      {hiddenCertifications.length > 0 && (
        <div className="my-certifications__hidden">
          <button
            type="button"
            className="dashboard__matches-accordion"
            aria-expanded={showHidden}
            aria-controls="my-certifications-hidden-list"
            onClick={() => setShowHidden((prev) => !prev)}
          >
            <span className="dashboard__matches-accordion-label">
              Certificazioni nascoste ({hiddenCertifications.length})
            </span>
            <span className="dashboard__matches-accordion-icon" aria-hidden="true" />
          </button>

          {showHidden && (
            <div id="my-certifications-hidden-list">
              {restoreCertification.isError && (
                <Alert tone="error" onDismiss={() => restoreCertification.reset()}>
                  Operazione non riuscita. Riprova più tardi.
                </Alert>
              )}
              <DataTable
                rows={hiddenRows}
                columns={hiddenColumns}
                rowKey={(row) => row.key}
                emptyMessage="Nessuna certificazione nascosta."
                defaultSort={{ key: 'issuedAt', direction: 'desc' }}
                paginated={false}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
